import React, { useEffect, useState } from 'react';

const LoadingScreen = ({ isVisible, onComplete, variant = 'full' }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (isVisible) {
            const duration = variant === 'full' ? 4000 : 1500;
            const interval = 50;
            const increment = 100 / (duration / interval);

            const timer = setInterval(() => {
                setProgress(prev => {
                    const next = prev + increment;
                    if (next >= 100) {
                        clearInterval(timer);
                        setTimeout(() => {
                            onComplete && onComplete();
                        }, 200);
                        return 100;
                    }
                    return next;
                });
            }, interval);

            return () => clearInterval(timer);
        } else {
            setProgress(0);
        }
    }, [isVisible, onComplete, variant]);

    if (variant === 'page') {
        return (
            <>
                <style>{`
          .page-loading {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: var(--gray-200);
            z-index: var(--z-tooltip);
            opacity: ${isVisible ? 1 : 0};
            transition: opacity 0.3s ease;
            pointer-events: none;
          }

          .page-loading-bar {
            height: 100%;
            background: linear-gradient(90deg, var(--blue-600), var(--purple-600));
            width: ${progress}%;
            transition: width 0.3s ease;
            box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
          }
        `}</style>
                <div className="page-loading">
                    <div className="page-loading-bar"></div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{`
        .loading-screen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--white);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          transition: opacity 0.5s ease, visibility 0.5s ease;
        }

        [data-theme="dark"] .loading-screen {
          background: var(--gray-900);
        }

        .loading-screen.hidden {
          opacity: 0;
          visibility: hidden;
        }

        .loading-content {
          text-align: center;
          max-width: 600px;
          padding: 2rem;
        }

        .loading-logo-container {
          margin-bottom: 1.5rem;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.2s forwards;
        }

        .loading-logo-image {
          width: 80px;
          height: 80px;
          margin: 0 auto 1rem;
          display: block;
          filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
        }

        [data-theme="dark"] .loading-logo-image {
          filter: drop-shadow(0 4px 6px rgba(255, 255, 255, 0.1)) brightness(1.2);
        }

        .loading-logo {
          font-size: 3rem;
          font-weight: 800;
          color: var(--black);
          letter-spacing: -0.05em;
        }

        [data-theme="dark"] .loading-logo {
          color: var(--white);
        }

        .loading-definition {
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.8s forwards;
        }

        .definition-line {
          margin-bottom: 1rem;
          font-size: 1.125rem;
          color: var(--gray-700);
        }

        [data-theme="dark"] .definition-line {
          color: var(--gray-300);
        }

        .definition-line.type {
          font-style: italic;
          color: var(--blue-600);
          font-weight: 500;
        }

        [data-theme="dark"] .definition-line.type {
          color: var(--blue-400);
        }

        .definition-line.etymology {
          color: var(--gray-500);
          font-size: 1rem;
        }

        [data-theme="dark"] .definition-line.etymology {
          color: var(--gray-400);
        }

        .definition-line.meaning {
          color: var(--gray-900);
          font-weight: 600;
          font-size: 1.25rem;
          margin-top: 1.5rem;
        }

        [data-theme="dark"] .definition-line.meaning {
          color: var(--gray-100);
        }

        .loading-progress {
          width: 100%;
          max-width: 400px;
          height: 2px;
          background: var(--gray-200);
          border-radius: 1px;
          margin: 2rem auto 0;
          overflow: hidden;
        }

        [data-theme="dark"] .loading-progress {
          background: var(--gray-700);
        }

        .loading-bar {
          height: 100%;
          background: linear-gradient(90deg, var(--blue-600), var(--purple-600));
          border-radius: 1px;
          width: ${progress}%;
          transition: width 0.3s ease;
        }

        .loading-particles {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: var(--blue-600);
          border-radius: 50%;
          opacity: 0.3;
          animation: float 10s infinite ease-in-out;
        }

        [data-theme="dark"] .particle {
          background: var(--blue-400);
          opacity: 0.5;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-100px) translateX(50px);
          }
          50% {
            transform: translateY(-50px) translateX(-50px);
          }
          75% {
            transform: translateY(-150px) translateX(25px);
          }
        }

        .particle:nth-child(1) { left: 10%; top: 20%; animation-delay: 0s; }
        .particle:nth-child(2) { left: 20%; top: 80%; animation-delay: 1s; }
        .particle:nth-child(3) { left: 30%; top: 40%; animation-delay: 2s; }
        .particle:nth-child(4) { left: 40%; top: 60%; animation-delay: 3s; }
        .particle:nth-child(5) { left: 50%; top: 30%; animation-delay: 4s; }
        .particle:nth-child(6) { left: 60%; top: 70%; animation-delay: 5s; }
        .particle:nth-child(7) { left: 70%; top: 20%; animation-delay: 6s; }
        .particle:nth-child(8) { left: 80%; top: 50%; animation-delay: 7s; }
        .particle:nth-child(9) { left: 90%; top: 80%; animation-delay: 8s; }
        .particle:nth-child(10) { left: 95%; top: 10%; animation-delay: 9s; }

        @media (max-width: 768px) {
          .loading-logo-image {
            width: 60px;
            height: 60px;
          }

          .loading-logo {
            font-size: 2rem;
          }

          .definition-line {
            font-size: 1rem;
          }

          .definition-line.meaning {
            font-size: 1.125rem;
          }
        }
      `}</style>

            <div className={`loading-screen ${!isVisible ? 'hidden' : ''}`}>
                <div className="loading-particles">
                    {[...Array(10)].map((_, i) => (
                        <div key={i} className="particle"></div>
                    ))}
                </div>
                <div className="loading-content">
                    <div className="loading-logo-container">
                        <img 
                            src="/logo.png" 
                            alt="Angalifu Logo" 
                            className="loading-logo-image"
                        />
                        <div className="loading-logo">angalifu</div>
                    </div>
                    <div className="loading-definition">
                        <div className="definition-line type">adjective</div>
                        <div className="definition-line etymology">from Swahili: angalifu</div>
                        <div className="definition-line meaning">"careful, attentive, mindful"</div>
                    </div>
                    <div className="loading-progress">
                        <div className="loading-bar"></div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoadingScreen;
